import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Filter } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import './PerformanceTypeFilter.css';

export interface PerformanceType {
  id: number;
  name: string;
  description?: string;
  display_order: number;
  is_active: boolean;
}

export interface MusicFilterState {
  hideServiceMusic: boolean;
  selectedPerformanceTypes: number[]; // Array of performance type IDs
}

interface PerformanceTypeFilterProps {
  filterState: MusicFilterState;
  onFilterChange: (filterState: MusicFilterState) => void;
}

export const PerformanceTypeFilter: React.FC<PerformanceTypeFilterProps> = ({
  filterState,
  onFilterChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [performanceTypes, setPerformanceTypes] = useState<PerformanceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch performance types on mount
  useEffect(() => {
    const fetchPerformanceTypes = async () => {
      try {
        const response = await apiClient.get('/performance-types?active=true');
        setPerformanceTypes(response.data);
      } catch (error) {
        console.error('Error fetching performance types:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPerformanceTypes();
  }, []);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getDisplayText = () => {
    const { hideServiceMusic, selectedPerformanceTypes } = filterState;

    // If showing only specific performance types
    if (selectedPerformanceTypes.length > 0) {
      const names = selectedPerformanceTypes
        .map(id => performanceTypes.find(pt => pt.id === id)?.name)
        .filter(Boolean);

      if (names.length > 2) {
        return `${names.length} Types`;
      }
      return names.join(' + ');
    }

    // If hiding service music only
    if (hideServiceMusic) {
      return 'No Service Music';
    }

    // Default - showing everything
    return 'All Music';
  };

  const isFiltered = filterState.hideServiceMusic || filterState.selectedPerformanceTypes.length > 0;

  const handleServiceMusicToggle = () => {
    onFilterChange({
      ...filterState,
      hideServiceMusic: !filterState.hideServiceMusic
    });
  };

  const handlePerformanceTypeToggle = (typeId: number) => {
    const isSelected = filterState.selectedPerformanceTypes.includes(typeId);
    const newSelected = isSelected
      ? filterState.selectedPerformanceTypes.filter(id => id !== typeId)
      : [...filterState.selectedPerformanceTypes, typeId];

    onFilterChange({
      ...filterState,
      selectedPerformanceTypes: newSelected
    });
  };

  return (
    <div className="performance-type-filter" ref={dropdownRef}>
      <button
        className={`filter-button ${isOpen ? 'open' : ''} ${isFiltered ? 'filtered' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Filter by music category"
      >
        <Filter size={16} className="filter-icon" />
        <span className="filter-text">{getDisplayText()}</span>
        <ChevronDown size={14} className={`chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="filter-dropdown">
          <div className="filter-header">
            <h6>Show</h6>
          </div>

          {isLoading ? (
            <div className="filter-options">
              <div className="loading-message">Loading...</div>
            </div>
          ) : (
            <div className="filter-options">
              <label className="filter-option">
                <input
                  type="checkbox"
                  checked={filterState.hideServiceMusic}
                  onChange={handleServiceMusicToggle}
                  className="filter-checkbox"
                />
                <span className="checkmark"></span>
                <span className="option-text">Hide Service Music</span>
              </label>

              <div className="filter-divider"></div>

              {performanceTypes.map((type) => (
                <label key={type.id} className="filter-option">
                  <input
                    type="checkbox"
                    checked={filterState.selectedPerformanceTypes.includes(type.id)}
                    onChange={() => handlePerformanceTypeToggle(type.id)}
                    className="filter-checkbox"
                  />
                  <span className="checkmark"></span>
                  <span className="option-text">{type.name}</span>
                </label>
              ))}
            </div>
          )}

          {isFiltered && (
            <div className="filter-footer">
              <button
                className="clear-filter-btn"
                onClick={() => onFilterChange({ hideServiceMusic: false, selectedPerformanceTypes: [] })}
              >
                Show All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};